import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../../src/Context/AuthContext";
import { useNavigate } from "react-router-dom";

const EditModal = ({ isOpen, onClose, onSaved, policy }) => {
    const [newStatus, setNewStatus] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [newStatuses, setNewStatuses] = useState([]);
    const { logout } = useAuth();
    const navigate = useNavigate();

    const apiURL = import.meta.env.VITE_API;

    const handleClose = () => {
        setNewStatus("");
        setNote("");
        onClose();
    };

    useEffect(() => {
        if (!isOpen) return;

        const fetchStatuses = async () => {
            try {
                setStatusLoading(true);

                const res = await fetch(
                    `${apiURL}/underwriting/GetUnderwritingStatusesToUpdateTo`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json"
                        }
                    }
                );

                if (res.status === 401) {
                    logout();
                    navigate("/login");
                    return;
                }

                if (res.status === 403) {
                    navigate("/home");
                    return;
                }

                if (!res.ok) {
                    throw new Error("Failed to load statuses");
                }

                const data = await res.json();
                setNewStatuses(data || []);
            } catch (err) {
                console.error("Error loading statuses:", err);
                setNewStatuses([]);
            } finally {
                setStatusLoading(false);
            }
        };

        fetchStatuses();
    }, [isOpen, apiURL, logout, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!newStatus && !note.trim()) {
            alert("Please select a new status or enter a note.");
            return;
        }

        try {
            setLoading(true);

            const res = await fetch(`${apiURL}/underwriting/UpdatePolicy`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    polNo: policy.polNo,
                    newStatus,
                    note
                })
            });

            if (res.status === 401) {
                logout();
                navigate("/login");
                throw new Error("unauthorized");
            }

            if (res.status === 403) {
                navigate("/home");
                throw new Error("forbidden");
            }

            if (!res.ok) {
                throw new Error("Failed to update policy");
            }

            alert("Policy updated successfully!");

            if (onSaved) {
                await onSaved();
            }

            onClose();
        } catch (err) {
            console.error("Error updating policy:", err);
            alert("Error updating policy. Check console for details.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Edit Policy Status</h3>
                    <button className="close-button" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Policy Number:</label>
                            <p>{policy.polNo || "—"}</p>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Owner Name:</label>
                            <p>{policy.displayName || "—"}</p>
                        </div>

                        <div className="form-group">
                            <label>Current Status:</label>
                            <p>{policy.status || "—"} - {policy.clientDesc || policy.amrDesc}</p>
                        </div>

                        <div className="form-group">
                            <label htmlFor="newStatus">New Status:</label>
                            <select
                                id="newStatus"
                                className="filterInput"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                disabled={statusLoading}
                            >
                                <option value="">
                                    {statusLoading ? "Loading statuses..." : "Select new status"}
                                </option>
                                {newStatuses.map((s, i) => (
                                    <option key={i} value={s.status}>
                                        {s.status} - {s.clientDesc || s.amrDesc}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="note">Note:</label>
                            <textarea
                                id="note"
                                className="filterInput"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Enter a note about the status update"
                                rows={4}
                            />
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="baseButton"
                                disabled={loading || statusLoading}
                            >
                                {loading ? "Saving..." : "Submit"}
                            </button>
                            <button
                                type="button"
                                className="baseButton secondary"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EditModal;